import { type ParsedAsset } from '@aikoa/common'
import { useCurrentLanguage, useTranslations } from '@aikoa/i18n/react'
import { Link, type Href } from 'expo-router'
import { useMemo } from 'react'
import { Pressable, View } from 'react-native'
import { ActionButton, type Action } from '~/components/Actions'
import { PropertyCardImage } from '~/components/assets/PropertyCard'
import { PropertyStats, type PropertyStat } from '~/components/assets/PropertyStats'
import {
  collectAssetDetails,
  formatPropertyPrice,
  useStats,
  type BaseProps,
} from '~/components/assets/propertyUtils'
import {
  createHighlightItemsFromLeads,
  FeedHighlightItems,
  FeedHighlightsMoreCount,
  type MatchingLeadHighlight,
} from '~/components/feed/FeedHighlights'
import { Heading3 } from '~/components/typography/Titles'
import { Badge } from '~/components/ui/badge'
import { Dot, type DotVariant } from '~/components/ui/dot'
import { Skeleton } from '~/components/ui/skeleton'
import { Text } from '~/components/ui/text'
import { PropertyTypeBadge } from '~/components/ui/TypeBadge'
import { cn } from '~/lib/utils'

type RowBaseProps = BaseProps & {
  isSelected?: boolean
  actions?: Action[]
  showPropertyDetails?: boolean
  children?: React.ReactNode
  isPressable?: boolean
  notificationType?: DotVariant
  matchingLeads?: MatchingLeadHighlight[]
  totalMatchingLeadsCount?: number
  totalFeedbackResponsesCount?: number
  showTypeBadge?: boolean
  organisationName?: string | null
  realtorName?: string | null
}

export function PropertyListItem({
  asset,
  className,
  isSelected,
  actions,
  isLoading,
  showPropertyDetails = true,
  children,
  isPressable = true,
  notificationType,
  matchingLeads,
  totalMatchingLeadsCount,
  totalFeedbackResponsesCount,
  showTypeBadge = true,
  organisationName,
  realtorName,
}: RowBaseProps) {
  const { t } = useTranslations('property')
  const language = useCurrentLanguage()

  const { addressTitle, askingPrice } = useMemo(() => collectAssetDetails(asset), [asset])
  const stats = useStats(asset, true)

  const propertyActions = actions
  const primaryAction = propertyActions?.find((action) => action.key === 'primary')

  const cardAction = propertyActions?.find((action) => action.key === 'card')
  const mainButtonAction = primaryAction || cardAction || propertyActions?.[0]

  const mainCardAction = cardAction || primaryAction || propertyActions?.[0]
  const rowPressable = !!mainCardAction && isPressable

  const isProspect = asset.relations[0]?.relation_type === 'prospect'

  const askingPriceOnBottom =
    !!askingPrice &&
    !children &&
    (!propertyActions || propertyActions.length < 2) &&
    showPropertyDetails

  const priceValue = useMemo(
    () =>
      askingPrice
        ? `${askingPrice.price_type === 'estimated_price' ? '≈ ' : ''}${formatPropertyPrice(askingPrice.price, askingPrice.currency, language)}`
        : '',
    [askingPrice, language]
  )

  const relation = asset.relations[0]
  const relationType = relation?.relation_type ?? 'representation'
  const ownership =
    relation.is_own !== undefined ? (relation.is_own ? 'own' : 'organisation') : undefined

  const fullStats = useMemo(
    () =>
      !askingPriceOnBottom && !!askingPrice
        ? [
            ...stats,
            {
              key: 'price',
              icon: 'pricetag-outline',
              value: <Text className="whitespace-nowrap font-bold">{priceValue}</Text>,
            } satisfies PropertyStat,
          ]
        : stats,
    [priceValue, askingPriceOnBottom, stats, askingPrice]
  )

  const titleRow = (
    <View className={cn('gap-2 pr-2')}>
      <View className="flex-row items-center gap-2">
        <Heading3 className="leading-none">{addressTitle || asset.name}</Heading3>
        {notificationType && <Dot variant={notificationType} />}
      </View>
      {showTypeBadge && (
        <PropertyTypeBadge
          type={relationType}
          ownership={ownership}
          organisationName={organisationName}
          realtorName={realtorName}
          variant="inverted"
        />
      )}
    </View>
  )

  const statsRow = <PropertyStats stats={fullStats} variant="row" iconSize={12} />

  const moreCount =
    (totalMatchingLeadsCount ?? 0) +
    (totalFeedbackResponsesCount ?? 0) -
    Math.min(matchingLeads?.length ?? 0, 3)

  const hasHighlights = matchingLeads && matchingLeads.length > 0

  const highlights = matchingLeads ? (
    <View className="gap-2">
      {hasHighlights ? (
        <>
          <Text className="font-light text-xs text-secondary">
            {t(($) => $.highlights.newMatches)}
          </Text>
          <FeedHighlightItems
            items={createHighlightItemsFromLeads({
              leads: matchingLeads,
              maxTypeItems: 2,
            })}
            className="-mx-3"
            maxItems={3}
            moreCount={moreCount}
          />
        </>
      ) : (
        <>
          <View className="mr-3 flex-1 items-center justify-center rounded-md bg-muted/50 p-2">
            <Text className="font-light text-sm text-secondary">
              {t(($) => $.highlights.noNewMatches)}
            </Text>
          </View>
          {!!moreCount && moreCount > 0 && <FeedHighlightsMoreCount moreCount={moreCount} />}
        </>
      )}
    </View>
  ) : null

  const footerContent = (
    <View
      className={cn(
        'w-full shrink-0 flex-row items-center justify-between gap-2 self-end pr-3',
        hasHighlights && 'px-3 pb-3'
      )}>
      {!!mainButtonAction && (
        <ActionButton
          action={{
            ...mainButtonAction,
            buttonVariant:
              mainButtonAction.key !== 'primary'
                ? mainButtonAction.buttonVariant
                : isSelected
                  ? mainButtonAction.buttonVariant || 'default'
                  : 'outline',
            label:
              isSelected && mainButtonAction.key === 'primary'
                ? t(($) => $.actions.selected)
                : 'label' in mainButtonAction
                  ? mainButtonAction.label
                  : undefined,
            isWithinLink: true,
            buttonSize: mainButtonAction.buttonSize || 'sm',
          }}
          key={mainButtonAction.key}
        />
      )}
      {askingPriceOnBottom ? (
        <View className={cn('ml-auto items-end', !propertyActions && 'ml-0')}>
          <Heading3 className="text-right font-bold leading-none">{priceValue}</Heading3>
        </View>
      ) : isProspect && !askingPrice ? (
        <Badge
          variant="default"
          size="sm"
          textClassName="font-normal text-primary"
          className="bg-neutral-300">
          {t(($) => $.price.prospectPriceNotDisplayed)}
        </Badge>
      ) : null}
      {children && <View className="items-end">{children}</View>}
    </View>
  )

  const mainContent = (
    <View className={cn('relative flex-1 items-start justify-between gap-4 py-3')}>
      <View className={cn('w-full flex-1 grow gap-4')}>
        {titleRow}
        {showPropertyDetails && statsRow}
        {!hasHighlights && highlights}
      </View>
      {!hasHighlights && footerContent}
    </View>
  )

  const renderPressable = rowPressable && (!!mainCardAction?.href || !!mainCardAction?.onPress)
  const isDisabled = mainCardAction?.disabled ?? false

  const wrapperClassName = cn(
    'native:shadow-card mx-2 my-1 flex flex-auto items-stretch rounded-xl bg-card shadow-md shadow-primary/30',
    !isDisabled &&
      renderPressable &&
      'transition-all duration-150 hover:bg-muted/50 active:scale-[0.98] active:bg-muted/50',
    className
  )

  const imageElement = (
    <View
      className={cn(
        'relative w-[25%] shrink-0 overflow-hidden rounded-lg p-1.5 pr-0',
        !showPropertyDetails && 'w-[20%]'
      )}>
      <PropertyCardImage
        asset={asset}
        style={useMemo(
          () => ({
            width: 'auto',
            height: '100%',
            aspectRatio: !showPropertyDetails ? 1 / 1 : 13 / 16,
            borderRadius: 7,
            zIndex: 1,
          }),
          [showPropertyDetails]
        )}
      />
    </View>
  )

  const content = (
    <>
      <View className={cn('flex min-h-0 w-full flex-1 flex-row gap-3')}>
        {imageElement}
        {mainContent}
      </View>
      {hasHighlights && <View className="gap-4 px-3 pb-3 pt-2">{highlights}</View>}
      {hasHighlights && footerContent}
    </>
  )

  if (isLoading) {
    return <Skeleton className="flex-1">{content}</Skeleton>
  }

  if (!renderPressable) {
    return <View className={wrapperClassName}>{content}</View>
  }

  if (mainCardAction.href) {
    return (
      <Link href={mainCardAction.href as Href} disabled={isDisabled} className={wrapperClassName}>
        {content}
      </Link>
    )
  }

  return (
    <Pressable onPress={mainCardAction.onPress} disabled={isDisabled} className={wrapperClassName}>
      {content}
    </Pressable>
  )
}

type CompactProps = {
  asset: ParsedAsset
  className?: string
}

export function PropertyRowCompact({ asset, className }: CompactProps) {
  const { addressTitle } = useMemo(() => collectAssetDetails(asset), [asset])

  return (
    <View className={cn('pointer-events-none flex-1 flex-row items-center gap-3 px-1', className)}>
      <View className="relative h-8 w-8 shrink-0 overflow-hidden rounded-md">
        <PropertyCardImage
          asset={asset}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 6,
          }}
        />
      </View>
      <Heading3 className="text-base leading-none" numberOfLines={1}>
        {addressTitle || asset.name}
      </Heading3>
    </View>
  )
}
